/**
 * feat-305 — how a page declares itself to the assistant (ADR 0001 §10.6,
 * NOTES §7). Everything page-specific lives here: the typed context builder
 * and the page-supplied copy. The panel, hook, client and fake are generic.
 *
 * Scorecard (later) adds `scorecardPageContext()` + its copy here, a variant
 * to `PageContext` in types.ts, and one `<AssistantLauncher …/>`.
 */
import type { SphPageContext } from './types';

export interface AssistantPageCopy {
  /** Panel header title. */
  title: string;
  /** Panel header subtitle (full text also goes in `title`). */
  subtitle?: string;
  /** First name used in the placeholder/heading/intro. Optional: copy has a no-subject fallback. */
  subjectName?: string;
  /** Full name for the textarea's sr-only label. */
  subjectFullName?: string;
  emptyHeading: string;
  emptyIntro: string;
  starters: string[];
  footerNote: string;
  /** Band button label, and the floating pill label at `sm+`. */
  entryLabel: string;
}

/**
 * Build the SPH context. `tenantId` MUST come from auth context (feat-133:
 * `scopedTenantId`, never route-state `serverData.tenantId`); `serverId` from
 * `location.state.serverData`. Returns null when either is missing, and the
 * launcher then renders nothing. Dates are the selected month's bounds
 * (YYYY-MM-DD) or null for all-time.
 */
export function sphPageContext(args: {
  tenantId: string | null | undefined;
  serverId: string | null | undefined;
  startDate?: string | null;
  endDate?: string | null;
}): SphPageContext | null {
  if (!args.tenantId || !args.serverId) return null;
  return {
    page: 'server_performance_history',
    tenant_id: args.tenantId,
    server_id: args.serverId,
    location_id: null,
    start_date: args.startDate ?? null,
    end_date: args.endDate ?? null,
  };
}

/** The spec's four starter questions (spec behavior 2). */
export const SPH_STARTERS: string[] = [
  'What are the highlights from the last two months?',
  'Where could this server improve?',
  'How has their greeting score trended?',
  'What should I focus on in their next coaching conversation?',
];

export const SPH_ENTRY_LABEL = 'Ask about this server';

export const SPH_FOOTER_NOTE =
  'AI answers can be wrong, so check numbers against this page. What was said on a shift stays private to the server.';

/** First word of a display name; undefined when there is none. */
export function firstNameOf(name: string | null | undefined): string | undefined {
  const first = (name || '').trim().split(/\s+/)[0];
  return first || undefined;
}

/** Page-supplied copy for Server Performance History (NOTES §3, §7). */
export function sphAssistantCopy(serverName?: string | null, locationName?: string | null): AssistantPageCopy {
  const full = (serverName || '').trim() || undefined;
  const first = firstNameOf(full);
  const who = first ?? 'this server';
  return {
    title: SPH_ENTRY_LABEL,
    subtitle: full ? (locationName ? `${full} · ${locationName}` : full) : locationName || undefined,
    subjectName: first,
    subjectFullName: full,
    emptyHeading: `What would you like to know about ${who}?`,
    emptyIntro: first
      ? `I can look at ${first}'s scores, metrics and shift summaries, and suggest what to work on together.`
      : "I can look at this server's scores, metrics and shift summaries, and suggest what to work on together.",
    starters: SPH_STARTERS,
    footerNote: SPH_FOOTER_NOTE,
    entryLabel: SPH_ENTRY_LABEL,
  };
}
